import {
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical'
import React from 'react'
import styled from 'styled-components'

import { MentionableEntry } from './useMentionableEntries'

const UnitMentionChip = styled.span`
  background: ${({ theme }) => theme.forecast.primary}20;
  color: ${({ theme }) => theme.forecast.primary};
  padding: 0.125rem 0.375rem;
  border-radius: 0.75rem;
  font-weight: 500;
  font-size: 0.875rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0 0.125rem;
  white-space: nowrap;

  &::before {
    content: '@';
    font-weight: 600;
  }
`

function convertUnitMentionElement(domNode: HTMLElement): DOMConversionOutput {
  const unitId = domNode.getAttribute('data-unit-id') || ''
  const unitName = domNode.getAttribute('data-unit-name') || ''
  if (unitId && unitName) {
    return { node: new UnitMentionNode({ unitId, unitName }) }
  }
  return { node: null }
}

export type SerializedUnitMentionNode = Spread<
  {
    unitId: string
    unitName: string
  },
  SerializedLexicalNode
>

export interface UnitMentionPayload {
  unitId: string
  unitName: string
}

/**
 * Inline decorator node representing an @-mentioned asset (unit). Renders a
 * pill showing the unit name and carries the underlying `unitId` so the chat
 * input can extract the set of mentioned units on submit.
 */
export class UnitMentionNode extends DecoratorNode<React.JSX.Element> {
  __unitId: string
  __unitName: string

  static getType(): string {
    return 'unit-mention'
  }

  static clone(node: UnitMentionNode): UnitMentionNode {
    return new UnitMentionNode(
      { unitId: node.__unitId, unitName: node.__unitName },
      node.__key
    )
  }

  constructor(payload: UnitMentionPayload, key?: NodeKey) {
    super(key)
    this.__unitId = payload.unitId
    this.__unitName = payload.unitName
  }

  getUnitId(): string {
    return this.__unitId
  }

  exportJSON(): SerializedUnitMentionNode {
    return {
      unitId: this.__unitId,
      unitName: this.__unitName,
      type: 'unit-mention',
      version: 1,
    }
  }

  static importJSON(
    serializedNode: SerializedUnitMentionNode
  ): UnitMentionNode {
    return new UnitMentionNode({
      unitId: serializedNode.unitId,
      unitName: serializedNode.unitName,
    })
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-unit-mention')) {
          return null
        }
        return { conversion: convertUnitMentionElement, priority: 1 }
      },
    }
  }

  createDOM(): HTMLElement {
    const element = document.createElement('span')
    element.setAttribute('data-lexical-unit-mention', 'true')
    element.setAttribute('data-unit-id', this.__unitId)
    element.setAttribute('data-unit-name', this.__unitName)
    return element
  }

  updateDOM(): false {
    return false
  }

  decorate(): React.JSX.Element {
    return (
      <UnitMentionChip title={this.__unitId}>{this.__unitName}</UnitMentionChip>
    )
  }

  getTextContent(): string {
    return `@${this.__unitName}`
  }

  isInline(): boolean {
    return true
  }

  isKeyboardSelectable(): boolean {
    return true
  }
}

export const createUnitMentionNode = (
  entry: MentionableEntry
): UnitMentionNode => {
  return new UnitMentionNode({ unitId: entry.id, unitName: entry.name })
}

export const $isUnitMentionNode = (
  node: LexicalNode | null | undefined
): node is UnitMentionNode => {
  return node instanceof UnitMentionNode
}
